import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../../hooks/useApi';
import {
  Settings as GeneralIcon,
  MapOutlined as MarketcompassIcon,
  Store as StoreIcon,
  AdminPanelSettings as AdminIcon,
  Webhook as WebhookIcon,
  Analytics as AnalyticsIcon,
  Hub as HubspotIcon,
} from '@mui/icons-material';

interface ApiGroupCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  path: string;
}

const API_GROUPS: ApiGroupCard[] = [
  {
    id: 'general',
    title: 'General',
    description: 'Core API endpoints for user information and system status',
    icon: <GeneralIcon sx={{ fontSize: 60 }} />,
    color: '#1976d2',
    path: '/admin/api-debugging/general'
  },
  {
    id: 'marketcompass',
    title: 'MarketCompass',
    description: 'Market access, subscriptions, reports, and podcasts',
    icon: <MarketcompassIcon sx={{ fontSize: 60 }} />,
    color: '#388e3c',
    path: '/admin/api-debugging/marketcompass'
  },
  {
    id: 'store',
    title: 'Store',
    description: 'E-commerce endpoints for subscriptions, markets, and Stripe integration',
    icon: <StoreIcon sx={{ fontSize: 60 }} />,
    color: '#f57c00',
    path: '/admin/api-debugging/store'
  },
  {
    id: 'marketcompass-admin',
    title: 'MarketCompass Admin',
    description: 'Administrative endpoints for managing reports and analytics',
    icon: <AdminIcon sx={{ fontSize: 60 }} />,
    color: '#c62828',
    path: '/admin/api-debugging/marketcompass-admin'
  },
  {
    id: 'webhooks',
    title: 'Webhooks',
    description: 'Webhook endpoints for Stripe and HubSpot integrations',
    icon: <WebhookIcon sx={{ fontSize: 60 }} />,
    color: '#0097a7',
    path: '/admin/api-debugging/webhooks'
  },
  {
    id: 'market-analytics',
    title: 'Market Analytics',
    description: 'Hotel market performance data and analytics endpoints',
    icon: <AnalyticsIcon sx={{ fontSize: 60 }} />,
    color: '#5e35b1',
    path: '/admin/api-debugging/market-analytics'
  },
  {
    id: 'hubspot',
    title: 'HubSpot',
    description: 'CRM integration for deals, companies, contacts, and listings',
    icon: <HubspotIcon sx={{ fontSize: 60 }} />,
    color: '#ff6b35',
    path: '/admin/api-debugging/hubspot'
  },
];

export const ApiDebuggingLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    if (currentUser) {
      const hasDevToolsPermission = currentUser.permissions?.includes('developer_tools.view');
      
      if (!hasDevToolsPermission) {
        navigate('/access-denied');
      }
    }
  }, [currentUser, navigate]);

  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  const hasDevToolsPermission = currentUser.permissions?.includes('developer_tools.view');

  if (!hasDevToolsPermission) {
    return null;
  }

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dev Tools Interface
        </Typography>
        
        <Alert severity="info" sx={{ mb: 4 }}>
          Select an API category below to test and debug endpoints. Each category contains related endpoints 
          that you can execute with custom parameters and view responses.
        </Alert>

        <Grid container spacing={3}>
          {API_GROUPS.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(group.path)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ color: group.color, mb: 2 }}>
                      {group.icon}
                    </Box>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {group.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};